/**
 * Patch-test positivity scale — `docs/comparison-emrx.md` §5.2, ported from `pacal/emrx`'s
 * `src/mock/patchtest.ts:83-95` (`STRENGTH`, `isAllergic`) and `:213-214` (`reportable`, the day-2
 * guard). Values and ordering only; `emrx`'s own generator/selection logic around this (mock-data
 * synthesis) is not ported — only the clinical rule is.
 *
 * TWO RULES, BOTH REAL CLINICAL KNOWLEDGE:
 *
 *   1. **`IR` sits outside the positive ordering.** An irritant reaction is a *negative* result with
 *      a confounder — it is not a weak positive, and grading it on the same axis as `+` is the
 *      classic misreading a patch-test scale exists to prevent. `STRENGTH` below gives it the same
 *      numeric weight as a plain negative (`0`), never a point between `-` and `?+`.
 *   2. **A positive cannot be called from a day-2 read alone (the day-2 guard).** Day 2 is
 *      applied-and-removed; a reaction visible then may still be irritant and may still fade. Only a
 *      later read (day 4 or day 7) can call a result. `reportableReading` below only ever looks at
 *      the latest *available* read that is not day 2 — it structurally cannot return a day-2 value as
 *      "the" result, which is what makes the guard a property of the function rather than a
 *      discipline the caller has to remember.
 */

/** The ordinal patch-test reading scale. `NT` (not tested) and `IR` (irritant) are not on the axis. */
export const PATCH_READINGS = ['NT', '-', 'IR', '?+', '+', '++', '+++'] as const;
export type PatchReading = (typeof PATCH_READINGS)[number];

export function isPatchReading(value: string): value is PatchReading {
  return (PATCH_READINGS as readonly string[]).includes(value);
}

/**
 * Ordinal strength, ported verbatim from `emrx`. `IR` is pinned to `0`, the same weight as a plain
 * negative — deliberately not given its own slot between `-` and `?+`, which would put it back on
 * the ordering the whole point of this table is to keep it off. `NT` sorts below everything so an
 * un-read allergen never outranks a real reading.
 */
export const PATCH_READING_STRENGTH: Readonly<Record<PatchReading, number>> = {
  NT: -1,
  '-': 0,
  IR: 0,
  '?+': 1,
  '+': 2,
  '++': 3,
  '+++': 4,
};

/** `+` or stronger. `?+` (doubtful) and everything at or below it, including `IR`, is not allergic. */
export function isAllergicReading(reading: PatchReading): boolean {
  return PATCH_READING_STRENGTH[reading] >= 2;
}

/** The three scheduled read days for a standard series (T.R.U.E. Test convention). */
export const PATCH_READ_DAYS = [2, 4, 7] as const;
export type PatchReadDay = (typeof PATCH_READ_DAYS)[number];

/** What has been read for one allergen so far. A day with no entry is treated as not yet read. */
export interface PatchReadingsByDay {
  readonly day2?: PatchReading;
  readonly day4?: PatchReading;
  readonly day7?: PatchReading;
}

/**
 * The reportable reading for one allergen — the day-2 guard, as a function rather than a discipline.
 *
 * Prefers day 7, then day 4, and **never day 2**: an allergen read only at day 2 has no reportable
 * result yet (`'NT'`), regardless of what that day-2 reading was. This is the direct port of
 * `emrx`'s own `reportable()`, whose header names the day-2-only case as "the first version's bug and
 * ... the exact clinical error the late reads exist to prevent."
 *
 * A day recorded as `'NT'` (not tested) is treated the same as absent — `readByDay.day7` present but
 * equal to `'NT'` falls through to day 4, matching a panel where the day-7 slot exists but has not
 * actually been read yet.
 */
export function reportableReading(readByDay: PatchReadingsByDay): PatchReading {
  if (readByDay.day7 !== undefined && readByDay.day7 !== 'NT') return readByDay.day7;
  if (readByDay.day4 !== undefined && readByDay.day4 !== 'NT') return readByDay.day4;
  return 'NT';
}

/** Whether this allergen currently has a reportable positive — the day-2 guard applied end to end. */
export function isReportablePositive(readByDay: PatchReadingsByDay): boolean {
  return isAllergicReading(reportableReading(readByDay));
}
